import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Highlighter, 
  X,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Type,
  Save,
  RotateCcw
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { marked } from 'marked';
import TurndownService from 'turndown';

// A single, module-level TurndownService instance handles HTML -> Markdown
// conversion using a real DOM parser instead of hand-rolled regexes. Regexes
// like `<strong[^>]*>(.*?)<\/strong>/gi` can't correctly handle nested tags or
// formatting that spans multiple block-level elements (e.g. bolding a
// selection that crosses a heading + paragraph + list item boundary), which
// produced malformed Markdown (stray/unbalanced `**`, `##` markers trapped
// inside `**...**`) that `marked()` could not re-render — showing up as
// literal, unstyled `**`/`##` characters instead of bold/heading formatting.
const turndownService = new TurndownService({
  headingStyle: 'atx',
  hr: '---',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  emDelimiter: '*',
  strongDelimiter: '**',
});

// turndown's core does not support strikethrough (that's normally provided by
// turndown-plugin-gfm, which isn't installed) — add it explicitly.
turndownService.addRule('strikethrough', {
  filter: ['del', 's', 'strike'],
  replacement: (content) => `~~${content}~~`,
});

// Standard Markdown (and marked's GFM mode) has no underline syntax — `__text__`
// is CommonMark for *bold*, not underline, so the old regex-based converter
// (`<u> -> __$1__`) silently turned underlined text into bold on the next
// render. Preserve <u> as raw inline HTML instead, which marked() passes
// through unchanged.
turndownService.addRule('preserveUnderline', {
  filter: 'u',
  replacement: (content) => `<u>${content}</u>`,
});

// Preserve custom highlight spans as raw HTML (same convention the rest of
// this file already relies on) instead of letting turndown drop the wrapper
// and keep only the plain text.
const HIGHLIGHT_CLASS_RE = /highlight-(yellow|green|blue|pink|orange)/;
turndownService.addRule('preserveHighlightSpans', {
  filter: (node) =>
    node.nodeName === 'SPAN' && HIGHLIGHT_CLASS_RE.test(node.getAttribute('class') || ''),
  replacement: (content, node) => {
    const match = (node.getAttribute('class') || '').match(HIGHLIGHT_CLASS_RE);
    const color = match ? match[1] : 'yellow';
    return `<span class="highlight-${color}">${content}</span>`;
  },
});

const WYSIWYGEditor = ({ 
  value, 
  onChange, 
  onSave,
  hasUnsavedChanges = false,
  placeholder = 'Start typing your content...',
  height = 500
}) => {
  const [content, setContent] = useState(value || '');
  const [htmlContent, setHtmlContent] = useState('');
  const editorRef = useRef(null);
  // Tracks the last Markdown value *this editor* emitted via onChange. The
  // parent is a controlled component that echoes it straight back down as the
  // `value` prop — without this, every keystroke/format action would trigger
  // the sync effect below and fully discard + rebuild the live contentEditable
  // DOM from `marked()`-rendered HTML, which is what caused formatting
  // (bold/italic/underline/strikethrough) to become corrupted mid-edit.
  const lastEmittedValueRef = useRef(null);

  const highlightColors = [
    { name: 'yellow', class: 'highlight-yellow', color: '#fef08a', border: '#eab308' },
    { name: 'green', class: 'highlight-green', color: '#bbf7d0', border: '#22c55e' },
    { name: 'blue', class: 'highlight-blue', color: '#bfdbfe', border: '#3b82f6' },
    { name: 'pink', class: 'highlight-pink', color: '#fecdd3', border: '#ec4899' },
    { name: 'orange', class: 'highlight-orange', color: '#fed7aa', border: '#f97316' }
  ];

  // Convert markdown to HTML
  const convertMarkdownToHtml = useCallback((markdown) => {
    if (!markdown) return '';
    
    // Configure marked to preserve line breaks and handle HTML
    marked.setOptions({
      breaks: true,
      gfm: true,
      headerIds: false,
      mangle: false
    });
    
    let html = marked(markdown);
    
    // Preserve existing highlight spans if they exist in the markdown
    // This handles cases where the markdown already contains highlight spans
    if (markdown.includes('<span class="highlight-')) {
      // Extract highlight spans from original markdown and preserve them
      const highlightRegex = /<span class="highlight-(yellow|green|blue|pink|orange)">(.*?)<\/span>/g;
      let match;
      while ((match = highlightRegex.exec(markdown)) !== null) {
        const [fullMatch, color, text] = match;
        const escapedText = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const htmlRegex = new RegExp(`(>|^)${escapedText}(<|$)`, 'g');
        html = html.replace(htmlRegex, `$1<span class="highlight-${color}">${text}</span>$2`);
      }
    }
    
    return html;
  }, []);

  // Convert HTML back to markdown using turndown (real DOM parsing) instead of
  // the previous flat regex replacements, which broke on nested tags and on
  // formatting selections that spanned multiple block-level elements.
  const convertHtmlToMarkdown = useCallback((html) => {
    if (!html) return '';

    try {
      return turndownService.turndown(html).trim();
    } catch (e) {
      console.error('Markdown conversion failed, keeping previous content:', e);
      return content;
    }
  }, [content]);

  useEffect(() => {
    // If this `value` update is just our own onChange echoing back down
    // (parent stores it in state and passes it straight back as a prop),
    // skip re-parsing it — the live DOM already reflects it, and rebuilding
    // from `marked()`-rendered HTML would discard the cursor position and
    // any DOM structure `execCommand` just created, which is what let
    // Markdown round-trip corruption compound on every keystroke.
    if (value === lastEmittedValueRef.current) {
      return;
    }
    setContent(value || '');
    const html = convertMarkdownToHtml(value || '');
    setHtmlContent(html);
  }, [value, convertMarkdownToHtml]);

  const handleContentChange = useCallback((newHtmlContent) => {
    // Deliberately do NOT call setHtmlContent(newHtmlContent) here. `htmlContent`
    // feeds `dangerouslySetInnerHTML` on the live contentEditable div below —
    // updating it on every keystroke/format action forces React to reassign
    // `.innerHTML` on that DOM node on the next render, which destroys and
    // recreates all of its child nodes. The browser has no way to preserve the
    // caret position across that, so it resets to the very start of the
    // editor — this is exactly the "cursor teleports to the top" bug.
    // The live DOM (editorRef.current) already reflects what was just typed —
    // React doesn't need to re-render it back into itself. `htmlContent` state
    // is only ever meant to *seed* the DOM from an external Markdown value
    // (initial mount, section switch, Cancel) — see the sync effect above.
    
    // Convert HTML back to markdown for storage
    const markdown = convertHtmlToMarkdown(newHtmlContent);
    setContent(markdown);
    lastEmittedValueRef.current = markdown;
    
    if (onChange) {
      onChange(markdown);
    }
  }, [onChange, convertHtmlToMarkdown]);

  // Function to normalize content and remove nested highlights
  const normalizeContent = useCallback(() => {
    if (!editorRef.current) return;
    
    const editor = editorRef.current;
    const highlightSpans = editor.querySelectorAll('.highlight-yellow, .highlight-green, .highlight-blue, .highlight-pink, .highlight-orange');
    
    highlightSpans.forEach(span => {
      // Check if this span contains other highlight spans
      const nestedHighlights = span.querySelectorAll('.highlight-yellow, .highlight-green, .highlight-blue, .highlight-pink, .highlight-orange');
      if (nestedHighlights.length > 0) {
        // Remove nested highlights and keep only the outer one
        nestedHighlights.forEach(nested => {
          const textNode = document.createTextNode(nested.textContent);
          nested.parentNode.replaceChild(textNode, nested);
        });
      }
    });
  }, []);

  const applyHighlight = useCallback((colorClass) => {
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();

    if (!selectedText) {
      toast.error('Please select some text to highlight');
      return;
    }

    const range = selection.getRangeAt(0);
    
    // Create a temporary container to work with the selection
    const tempContainer = document.createElement('div');
    tempContainer.appendChild(range.cloneContents());
    
    // Remove all existing highlights from the cloned content
    const existingHighlights = tempContainer.querySelectorAll('.highlight-yellow, .highlight-green, .highlight-blue, .highlight-pink, .highlight-orange');
    existingHighlights.forEach(highlight => {
      const textNode = document.createTextNode(highlight.textContent);
      highlight.parentNode.replaceChild(textNode, highlight);
    });
    
    // Get the clean text without any highlights
    const cleanText = tempContainer.textContent;
    
    // Delete the original selection
    range.deleteContents();
    
    // Create new highlight span with clean text
    const span = document.createElement('span');
    span.className = colorClass;
    span.textContent = cleanText;
    
    // Insert the new highlight
    range.insertNode(span);
    
    // Normalize content to prevent any layering
    normalizeContent();
    
    // Update content
    const newHtmlContent = editorRef.current.innerHTML;
    handleContentChange(newHtmlContent);

    // Clear selection
    window.getSelection().removeAllRanges();

    toast.success(`Text highlighted with ${colorClass.replace('highlight-', '')}`);
  }, [handleContentChange, normalizeContent]);

  const removeHighlight = useCallback(() => {
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();

    if (!selectedText) {
      toast.error('Please select highlighted text to remove');
      return;
    }

    const range = selection.getRangeAt(0);
    
    // Find all highlight spans within the selection
    const highlightSpans = editorRef.current.querySelectorAll('.highlight-yellow, .highlight-green, .highlight-blue, .highlight-pink, .highlight-orange');
    let foundHighlight = false;
    
    highlightSpans.forEach(span => {
      // Check if this span intersects with our selection
      if (range.intersectsNode(span)) {
        // Replace the highlight span with its text content
        const textNode = document.createTextNode(span.textContent);
        span.parentNode.replaceChild(textNode, span);
        foundHighlight = true;
      }
    });
    
    if (foundHighlight) {
      // Normalize content to prevent any layering
      normalizeContent();
      
      // Update content
      const newHtmlContent = editorRef.current.innerHTML;
      handleContentChange(newHtmlContent);
      
      // Clear selection
      window.getSelection().removeAllRanges();
      
      toast.success('Highlight removed');
    } else {
      toast.error('No highlight found in selected text');
    }
  }, [handleContentChange, normalizeContent]);

  // Add periodic cleanup to prevent layering
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      normalizeContent();
    }, 2000); // Clean up every 2 seconds

    return () => clearInterval(cleanupInterval);
  }, [normalizeContent]);

  const applyFormatting = useCallback((format) => {
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();

    if (!selectedText) {
      toast.error('Please select some text to format');
      return;
    }

    switch (format) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      case 'italic':
        document.execCommand('italic', false);
        break;
      case 'underline':
        document.execCommand('underline', false);
        break;
      case 'strikethrough':
        document.execCommand('strikeThrough', false);
        break;
      case 'code':
        document.execCommand('insertHTML', false, `<code>${selectedText}</code>`);
        break;
      default:
        return;
    }

    // Update content
    const newHtmlContent = editorRef.current.innerHTML;
    handleContentChange(newHtmlContent);

    toast.success(`Text formatted as ${format}`);
  }, [handleContentChange]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  const handleKeyDown = useCallback((e) => {
    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          if (e.shiftKey) {
            e.preventDefault();
            applyHighlight('highlight-blue');
          } else {
            e.preventDefault();
            applyFormatting('bold');
          }
          break;
        case 'i':
          e.preventDefault();
          applyFormatting('italic');
          break;
        case 'u':
          e.preventDefault();
          applyFormatting('underline');
          break;
        case 's':
          e.preventDefault();
          applyFormatting('strikethrough');
          break;
        case 'y':
          if (e.shiftKey) {
            e.preventDefault();
            applyHighlight('highlight-yellow');
          }
          break;
        case 'g':
          if (e.shiftKey) {
            e.preventDefault();
            applyHighlight('highlight-green');
          }
          break;
        case 'p':
          if (e.shiftKey) {
            e.preventDefault();
            applyHighlight('highlight-pink');
          }
          break;
        case 'o':
          if (e.shiftKey) {
            e.preventDefault();
            applyHighlight('highlight-orange');
          }
          break;
        case 'r':
          if (e.shiftKey) {
            e.preventDefault();
            removeHighlight();
          }
          break;
      }
    }
  }, [applyFormatting, applyHighlight, removeHighlight]);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(content);
    }
  }, [onSave, content]);

  return (
    <div className="relative">
      {/* Editor Toolbar */}
      <div className="bg-white border border-gray-300 rounded-t-lg p-3 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => applyFormatting('bold')}
            className="p-2 rounded hover:bg-gray-100 transition-colors"
            title="Bold (Ctrl+B)"
          >
            <Bold size={16} />
          </button>
          <button
            onClick={() => applyFormatting('italic')}
            className="p-2 rounded hover:bg-gray-100 transition-colors"
            title="Italic (Ctrl+I)"
          >
            <Italic size={16} />
          </button>
          <button
            onClick={() => applyFormatting('underline')}
            className="p-2 rounded hover:bg-gray-100 transition-colors"
            title="Underline (Ctrl+U)"
          >
            <Underline size={16} />
          </button>
          <button
            onClick={() => applyFormatting('strikethrough')}
            className="p-2 rounded hover:bg-gray-100 transition-colors"
            title="Strikethrough (Ctrl+S)"
          >
            <Strikethrough size={16} />
          </button>
          <button
            onClick={() => applyFormatting('code')}
            className="p-2 rounded hover:bg-gray-100 transition-colors"
            title="Code"
          >
            <Type size={16} />
          </button>
          
          <div className="w-px h-6 bg-gray-300 mx-2"></div>
          
          <Highlighter size={16} className="text-gray-500" />
          <span className="text-sm text-gray-500">Highlight:</span>
          {highlightColors.map((color) => (
            <button
              key={color.name}
              onClick={() => applyHighlight(color.class)}
              className="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-400 transition-colors"
              style={{
                backgroundColor: color.color,
                borderColor: color.border
              }}
              title={`${color.name} highlight (Ctrl+Shift+${color.name.charAt(0).toUpperCase()})`}
            />
          ))}
          <button
            onClick={removeHighlight}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
            title="Remove highlight (Ctrl+Shift+R)"
          >
            <X size={14} />
          </button>
        </div>
        
        <div className="flex items-center space-x-2">
          {hasUnsavedChanges && (
            <span className="text-sm text-amber-600 flex items-center">
              <RotateCcw className="w-4 h-4 mr-1 animate-spin" />
              Unsaved changes
            </span>
          )}
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center"
            title="Save (Ctrl+S)"
          >
            <Save size={16} className="mr-2" />
            Save
          </button>
        </div>
      </div>

      {/* Editor Content - Always in Edit Mode */}
      <div
        ref={editorRef}
        className="wysiwyg-editor bg-white border border-gray-300 border-t-0 rounded-b-lg p-4 min-h-[400px] max-h-[600px] overflow-y-auto focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        contentEditable={true}
        suppressContentEditableWarning={true}
        onInput={(e) => handleContentChange(e.currentTarget.innerHTML)}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        style={{ height }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />

      {/* Keyboard Shortcuts Help */}
      <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
        <div className="font-medium mb-1">Keyboard Shortcuts:</div>
        <div className="grid grid-cols-2 gap-1">
          <span>Ctrl+B: Bold</span>
          <span>Ctrl+I: Italic</span>
          <span>Ctrl+U: Underline</span>
          <span>Ctrl+S: Strikethrough</span>
          <span>Ctrl+Shift+Y: Yellow highlight</span>
          <span>Ctrl+Shift+G: Green highlight</span>
          <span>Ctrl+Shift+B: Blue highlight</span>
          <span>Ctrl+Shift+P: Pink highlight</span>
          <span>Ctrl+Shift+O: Orange highlight</span>
          <span>Ctrl+Shift+R: Remove highlight</span>
        </div>
      </div>
    </div>
  );
};

export default WYSIWYGEditor; 